import { VersionRange } from '../../core/version-range.js';
import { MapUtils } from '../../../utility/map-utils.js';
import {
  DataModel,
  DataModelBuilder,
  SerializationContext,
  SerializedData,
  isSameData,
  isSameList,
} from '../../dto/dto.js';
import { LocalizedString } from '../localized-string.js';
import { IDataWithLocalizedName } from '../../../contracts/localized-data.js';

export class TicketTemplateCategory extends DataModel implements IDataWithLocalizedName {
  static readonly TYPE_ID: string = '088dd61267a74984b7c94610484995a6';
  static readonly SCHEMA_VERSION: number = 3;

  constructor(
    public readonly uniqueId: string,
    public readonly names: LocalizedString,
    public readonly items: string[],
    public readonly expanded: boolean,
    public readonly showLowestPrice: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return TicketTemplateCategory.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, TicketTemplateCategory.SCHEMA_VERSION);
  }

  static create(args: {
    uniqueId: string
    names: LocalizedString,
    items: string[],
    expanded: boolean,
    showLowestPrice: boolean
  }): TicketTemplateCategory {
    return new TicketTemplateCategory(
        args.uniqueId,
        args.names,
        args.items,
        args.expanded,
        args.showLowestPrice,
    );
  }

  static empty(uniqueId: string): TicketTemplateCategory {
    return new TicketTemplateCategory(
        uniqueId,
        LocalizedString.empty(),
        new Array<string>(),
        false,
        true,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): TicketTemplateCategory {
    return new TicketTemplateCategory(
        MapUtils.getString(map, 'uniqueId'),
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'names')),
        MapUtils.getList<string>(map, 'items', (value) => value as string),
        MapUtils.getBoolean(map, 'expanded'),
        MapUtils.getBoolean(map, 'showLowestPrice'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'names': context.toData(this.names),
      'items': this.items,
      'expanded': this.expanded,
      'showLowestPrice': this.showLowestPrice,
    };
  }

  clone(): this {
    return new TicketTemplateCategory(
        this.uniqueId,
        this.names.clone(),
        this.items,
        this.expanded,
        this.showLowestPrice,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
       isSameData(other.names, this.names) &&
       isSameList<string>(other.items, this.items) &&
       other.expanded === this.expanded &&
       other.showLowestPrice === this.showLowestPrice;
  }
}

export class TicketTemplateCategoryBuilder extends DataModelBuilder<TicketTemplateCategory> {
  build(context: SerializationContext, map: SerializedData): TicketTemplateCategory {
    return TicketTemplateCategory.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(TicketTemplateCategory.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(TicketTemplateCategory.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
    * Version 2:
    *  - add `expanded` of type [bool]
    */
    if(version < 2) {
      map['expanded'] ??= false;
      version = 2;
    }

    /**
    * Version 3:
    *  - add `showLowestPrice` of type [bool]
    */
    if(version < 3) {
      map['showLowestPrice'] ??= true;
      version = 3;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
